import { z } from 'zod';

/**
 * The `VITE_*` contract, in one place because two very different consumers ask
 * the same question: `env.ts` at runtime in the browser, and `vite.config.ts`
 * at build time in Node. Two copies of this shape would be two chances for the
 * build gate to accept something the app then refuses.
 */
export const envSchema = z.object({
  // No default. A silent fallback here is how a production bundle ends up
  // pointing at the operator's own laptop: it compiles green and every screen
  // fails with NETWORK_ERROR in the browser, far from the mistake.
  VITE_API_URL: z.string().url(),
  VITE_ENV: z.enum(['development', 'staging', 'production']).default('development'),
  VITE_SENTRY_DSN: z.string().default(''),
});

export type PanelEnv = z.infer<typeof envSchema>;

/** Where the dev server and the test runner point when nothing is configured. */
export const DEV_API_URL = 'http://localhost:3000';

export function parseEnv(raw: unknown) {
  return envSchema.safeParse(raw);
}

/**
 * The build-time gate. A bundle is built once and served to every browser, so
 * a missing `VITE_API_URL` is not something the app can recover from at
 * runtime: it is baked in. Failing here is the only place the mistake is still
 * cheap.
 */
export function assertBuildEnv(raw: Record<string, string | undefined>): PanelEnv {
  const parsed = parseEnv(raw);
  if (!parsed.success) {
    const fields = parsed.error.flatten().fieldErrors;
    const lines = Object.entries(fields).map(([k, v]) => `  ${k}: ${(v ?? []).join(', ')}`);
    throw new Error(`Invalid VITE_ build env:\n${lines.join('\n')}`);
  }
  return parsed.data;
}
